import { Button, Card, Input, Label, Spinner } from '@fc/ui'
import { useMutation, useQuery } from '@tanstack/react-query'
import { CheckCircle2 } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ApiError } from '@fc/core'
import { BrandLockup } from '../../components/brand'
import { ThemeToggle } from '../../components/theme-toggle'
import {
  getPublicForm,
  submitPublicForm,
  type FormField,
  type PublicForm,
} from '../../lib/forms-api'

type AnswerValue = string | string[]

function FieldControl({
  field,
  value,
  error,
  onChange,
}: {
  field: FormField
  value: AnswerValue | undefined
  error: string | undefined
  onChange: (v: AnswerValue) => void
}) {
  const baseInput =
    'w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-fg placeholder:text-dim focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring'
  const id = `field-${field.id}`
  const describedBy = field.helpText ? `${id}-help` : undefined
  const options = field.config?.options ?? []

  const control = (() => {
    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            id={id}
            rows={4}
            value={(value as string) ?? ''}
            placeholder={field.placeholder ?? ''}
            aria-describedby={describedBy}
            aria-invalid={!!error}
            required={field.required}
            onChange={(e) => onChange(e.target.value)}
            className={baseInput}
          />
        )
      case 'select':
        return (
          <select
            id={id}
            value={(value as string) ?? ''}
            aria-describedby={describedBy}
            aria-invalid={!!error}
            required={field.required}
            onChange={(e) => onChange(e.target.value)}
            className="h-10 w-full rounded-lg border border-border bg-card px-3 text-sm text-fg focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
          >
            <option value="">Select…</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        )
      case 'radio':
        return (
          <div className="space-y-1.5" role="radiogroup" aria-labelledby={`${id}-label`}>
            {options.map((o) => (
              <label key={o.value} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name={id}
                  value={o.value}
                  checked={value === o.value}
                  onChange={() => onChange(o.value)}
                  className="size-4 accent-[var(--primary)]"
                />
                {o.label}
              </label>
            ))}
          </div>
        )
      case 'checkbox': {
        const arr = Array.isArray(value) ? value : []
        return (
          <div className="space-y-1.5">
            {options.map((o) => (
              <label key={o.value} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  value={o.value}
                  checked={arr.includes(o.value)}
                  onChange={(e) =>
                    onChange(
                      e.target.checked ? [...arr, o.value] : arr.filter((v) => v !== o.value),
                    )
                  }
                  className="size-4 rounded border-border accent-[var(--primary)]"
                />
                {o.label}
              </label>
            ))}
          </div>
        )
      }
      default: {
        const inputType =
          field.type === 'email'
            ? 'email'
            : field.type === 'phone'
              ? 'tel'
              : field.type === 'number'
                ? 'number'
                : field.type === 'date'
                  ? 'date'
                  : 'text'
        return (
          <Input
            id={id}
            type={inputType}
            value={(value as string) ?? ''}
            placeholder={field.placeholder ?? ''}
            aria-describedby={describedBy}
            aria-invalid={!!error}
            required={field.required}
            onChange={(e) => onChange(e.target.value)}
          />
        )
      }
    }
  })()

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} id={`${id}-label`}>
        {field.label}
        {field.required && <span className="ml-1 text-danger">*</span>}
      </Label>
      {control}
      {field.helpText && (
        <p id={describedBy} className="text-xs text-dim">
          {field.helpText}
        </p>
      )}
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  )
}

function FormFields({ form }: { form: PublicForm }) {
  const { orgSlug, formSlug } = useParams<{ orgSlug: string; formSlug: string }>()
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({})
  const [consent, setConsent] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [generalError, setGeneralError] = useState<string | null>(null)
  const [done, setDone] = useState<{ message: string | null } | null>(null)

  const consentRequired = Boolean(form.consentText) || form.fields.some((f) => f.type === 'consent')
  const renderableFields = form.fields.filter((f) => f.type !== 'consent')

  const submit = useMutation({
    mutationFn: () => submitPublicForm(orgSlug!, formSlug!, { answers, consent }),
    onSuccess: (res) => setDone({ message: res.confirmationMessage }),
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.fields) setFieldErrors(err.fields)
        setGeneralError(err.message)
      } else {
        setGeneralError(err instanceof Error ? err.message : 'Submission failed')
      }
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setFieldErrors({})
    setGeneralError(null)
    submit.mutate()
  }

  if (done) {
    return (
      <div className="space-y-3 py-10 text-center">
        <CheckCircle2 className="mx-auto size-10 text-success" aria-hidden="true" />
        <h2 className="font-display text-xl font-semibold">Thank you!</h2>
        <p className="text-sm text-muted">{done.message ?? 'Your response has been submitted.'}</p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      {renderableFields.map((field) => (
        <FieldControl
          key={field.id}
          field={field}
          value={answers[field.key]}
          error={fieldErrors[field.key]}
          onChange={(v) => setAnswers((prev) => ({ ...prev, [field.key]: v }))}
        />
      ))}

      {consentRequired && (
        <div className="space-y-1.5 rounded-lg border border-border bg-card p-3">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 size-4 shrink-0 rounded border-border accent-[var(--primary)]"
              aria-invalid={!!fieldErrors.consent}
            />
            <span>
              {form.consentText ?? 'I consent to this organization processing my submission.'}
              <span className="ml-1 text-danger">*</span>
            </span>
          </label>
          {fieldErrors.consent && <p className="text-xs text-danger">{fieldErrors.consent}</p>}
        </div>
      )}

      {generalError && (
        <div
          role="alert"
          className="rounded-lg border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger"
        >
          {generalError}
        </div>
      )}

      <Button type="submit" disabled={submit.isPending} className="w-full">
        {submit.isPending ? <Spinner /> : null}
        Submit
      </Button>
    </form>
  )
}

export function PublicFormPage() {
  const { orgSlug, formSlug } = useParams<{ orgSlug: string; formSlug: string }>()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-form', orgSlug, formSlug],
    queryFn: () => getPublicForm(orgSlug!, formSlug!),
    enabled: !!orgSlug && !!formSlug,
    retry: false,
  })

  return (
    <div className="min-h-dvh bg-bg">
      <header className="flex h-16 items-center justify-between border-b border-border px-4 sm:px-6">
        <BrandLockup />
        <ThemeToggle />
      </header>

      <main className="mx-auto w-full max-w-2xl px-4 py-10 sm:py-14">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading form…
          </div>
        ) : isError || !data ? (
          <Card className="space-y-2 py-16 text-center">
            <h1 className="font-display text-xl font-semibold">Form unavailable</h1>
            <p className="text-sm text-muted">
              This form doesn’t exist, isn’t published, or is no longer accepting responses.
            </p>
          </Card>
        ) : (
          <Card className="space-y-6 p-6 sm:p-8">
            <div className="space-y-2">
              <h1 className="font-display text-2xl font-bold tracking-tight">{data.name}</h1>
              {data.description && <p className="text-sm text-muted">{data.description}</p>}
            </div>
            <FormFields form={data} />
          </Card>
        )}
      </main>
    </div>
  )
}
